import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { Link } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';

const Footer = () => {
  const isMobile = useIsMobile();

  const handleEmailPress = (email) => {
    Linking.openURL(`mailto:${email}`);
  };

  const handlePhonePress = () => {
    Linking.openURL('tel:+15166602268');
  };

  const handleSocialPress = (url) => {
    Linking.openURL(url);
  };

  return (
    <View style={[styles.container, isMobile && styles.mobileContainer]}>
      <View style={[styles.content, isMobile && styles.mobileContent]}>
        {/* Company Info Section */}
        <View style={[styles.companySection, isMobile && styles.mobileCompanySection]}>
          <View style={styles.logoContainer}>
            <img 
              src="/zecher-logo.png" 
              alt="Zecher"
              style={styles.logoImage}
              onError={(e) => {
                // Fallback to text logo if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <View style={[styles.logoFallback, { display: 'none' }]}>
              <View style={styles.logoIcon}>
                <Text style={styles.logoText}>Z</Text>
              </View>
              <Text style={styles.companyName}>Zecher</Text>
            </View>
          </View>
          <Text style={styles.tagline}>by Zecher, Inc.</Text>
          
          <View style={styles.contactInfo}>
            <Text style={styles.contactLabel}>Say 👋 at </Text>
            <TouchableOpacity onPress={() => handleEmailPress('contact@zecher.ai')}>
              <Text style={styles.contactLink}>contact@zecher.ai</Text>
            </TouchableOpacity>
          </View>
          
          <View style={styles.address}>
            <Text style={styles.addressText}>8 The Green STE A</Text>
            <Text style={styles.addressText}>Dover, Kent County, DE 19901</Text>
          </View>

          <TouchableOpacity style={styles.phoneContainer} onPress={handlePhonePress}>
            <Text style={styles.phoneText}>(516) 660-2268</Text>
          </TouchableOpacity>
        </View>

        {/* Features Section */}
        <View style={[styles.linksSection, isMobile && styles.mobileLinksSection]}>
          <Text style={styles.sectionTitle}>Features</Text>
          <Link to="/accounts" style={styles.linkItem}>
            <Text style={styles.linkText}>Account Tracking</Text>
          </Link>
          <Link to="/spending" style={styles.linkItem}>
            <Text style={styles.linkText}>Spending Analysis</Text>
          </Link>
          <Link to="/budget" style={styles.linkItem}>
            <Text style={styles.linkText}>Budget Planning</Text>
          </Link>
          <Link to="/wealth-plan" style={styles.linkItem}>
            <Text style={styles.linkText}>Wealth Planning</Text>
          </Link>
          <Link to="/fact-finder" style={styles.linkItem}>
            <Text style={styles.linkText}>AI Insights</Text>
          </Link>
        </View>

        {/* Company Section */}
        <View style={[styles.linksSection, isMobile && styles.mobileLinksSection]}>
          <Text style={styles.sectionTitle}>Company</Text>
          <TouchableOpacity onPress={() => handleEmailPress('careers@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Careers</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleEmailPress('contact@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Contact us</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleEmailPress('press@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Press</Text>
          </TouchableOpacity>
        </View>

        {/* Product Section */}
        <View style={[styles.linksSection, isMobile && styles.mobileLinksSection]}>
          <Text style={styles.sectionTitle}>Product</Text>
          <TouchableOpacity style={styles.linkItem}>
            <Text style={styles.linkText}>Start free trial</Text>
          </TouchableOpacity>
          <Link to="/" style={styles.linkItem}>
            <Text style={styles.linkText}>Access your account</Text>
          </Link>
          <Link to="/terms-of-service" style={styles.linkItem}>
            <Text style={styles.linkText}>Terms of service</Text>
          </Link>
          <Link to="/privacy-policy" style={styles.linkItem}>
            <Text style={styles.linkText}>Privacy policy</Text>
          </Link>
        </View>

        {/* Resources Section */}
        <View style={[styles.linksSection, isMobile && styles.mobileLinksSection]}>
          <Text style={styles.sectionTitle}>Resources</Text>
          <TouchableOpacity onPress={() => handleEmailPress('help@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Help Center</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleEmailPress('support@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Support</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleEmailPress('security@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>Security</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleEmailPress('api@zecher.ai')} style={styles.linkItem}>
            <Text style={styles.linkText}>API Docs</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Bottom Section */}
      <View style={[styles.bottomSection, isMobile && styles.mobileBottomSection]}>
        <View style={styles.bottomContent}>
          <Text style={styles.copyrightText}>
            © 2025 Zecher, Inc. All rights reserved.
          </Text>
          <View style={styles.socialLinks}>
            <TouchableOpacity 
              onPress={() => handleSocialPress('https://linkedin.com/company/zecher-ai')}
              style={styles.socialButton}
            >
              <Text style={styles.socialIcon}>in</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => handleSocialPress('https://twitter.com/zecher_ai')}
              style={styles.socialButton}
            >
              <Text style={styles.socialIcon}>🐦</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => handleEmailPress('contact@zecher.ai')}
              style={styles.socialButton}
            >
              <Text style={styles.socialIcon}>✉️</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF', // Clean white background
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 48,
    paddingBottom: 24,
    marginTop: 'auto', // Push to bottom
    minHeight: 400, // Ensure good height
    width: '100%',
  },
  mobileContainer: {
    paddingTop: 32,
    paddingBottom: 16,
    marginTop: 'auto',
    minHeight: 350,
  },
  content: {
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 48,
  },
  mobileContent: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    gap: 32,
  },
  
  // Company Section
  companySection: {
    flex: 1,
    minWidth: 280,
    maxWidth: 320,
  },
  mobileCompanySection: {
    maxWidth: '100%',
    minWidth: '100%',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  logoImage: {
    height: 32,
    width: 'auto',
    maxWidth: 150,
    objectFit: 'contain',
  },
  logoFallback: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    width: 32,
    height: 32,
    backgroundColor: '#2563EB',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  logoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  tagline: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 24,
  },
  contactInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  contactLabel: {
    fontSize: 14,
    color: '#374151',
  },
  contactLink: {
    fontSize: 14,
    color: '#2563EB',
    textDecorationLine: 'underline',
  },
  address: {
    marginBottom: 12,
  },
  addressText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  phoneContainer: {
    marginTop: 8,
  },
  phoneText: {
    fontSize: 14,
    color: '#2563EB',
    textDecorationLine: 'underline',
  },
  
  // Links Sections
  linksSection: {
    flex: 1,
    minWidth: 150,
    maxWidth: 200,
  },
  mobileLinksSection: {
    maxWidth: '100%',
    minWidth: '100%',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  linkItem: {
    marginBottom: 12,
    textDecoration: 'none',
  },
  linkText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  
  // Bottom Section
  bottomSection: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginTop: 48,
    paddingTop: 24,
  },
  mobileBottomSection: {
    marginTop: 32,
    paddingTop: 16,
  },
  bottomContent: {
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  copyrightText: {
    fontSize: 14,
    color: '#6B7280',
  },
  socialLinks: {
    flexDirection: 'row',
    gap: 16,
  },
  socialButton: {
    width: 32,
    height: 32,
    backgroundColor: '#F3F4F6',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialIcon: {
    fontSize: 14,
    color: '#6B7280',
  },
});

export default Footer; 